/* OwnStore 3.0 local commerce. Prices/discounts/inventory are confirmed by Shopify.
 * Mutations are serialized. A failed POST is never automatically repeated.
 */
(() => {
  'use strict';
  const O = window.OwnStore;
  if (!O || O.cart) return;
  const text = (key, fallback) => O.config.strings?.[key] || fallback;
  const idEqual = (a, b) => String(a) === String(b);
  const domId = value => document.getElementById(value);
  const scopeRoot = el => O.owner(el) || el.closest('bundle-deals') || el.closest('.shopify-section') || document;
  const visibleError = (root, message) => {
    let box = root.matches('product-form') ? root.querySelector('.product-form__error-message-wrapper, [data-ownstore-error]') : root.querySelector('.cart-errors, #CartDrawer-CartErrors, :scope > [data-ownstore-error]');
    if (!box) { box = document.createElement('div'); box.dataset.ownstoreError = ''; box.className = 'ownstore-error'; box.setAttribute('role', 'alert'); root.append(box); }
    box.hidden = !message; const label = box.querySelector('.product-form__error-message') || box; label.textContent = message || '';
  };
  const aggregate = items => {
    const combined = new Map();
    for (const raw of items) {
      const item = { ...raw, id: String(raw.id), quantity: O.int(raw.quantity, 1) };
      if (!/^\d+$/.test(item.id) || item.quantity <= 0) throw new Error(text('option_quantity_required', 'Please select an available product option and a positive quantity.'));
      const props = item.properties || {};
      const key = JSON.stringify([item.id, item.selling_plan || '', Object.entries(props).sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => [k, v instanceof File ? v.name : v])]);
      if (combined.has(key)) combined.get(key).quantity += item.quantity; else combined.set(key, item);
    }
    return [...combined.values()];
  };
  O.aggregateItems = aggregate;
  class CartService {
    constructor() { this.state = null; this.tail = Promise.resolve(); this.pending = 0; this.warning = ''; this.suppressedGifts = new Set(); }
    sectionRequests() {
      const items = [];
      const drawer = document.querySelector('cart-drawer');
      if (drawer) items.push({ id: drawer.dataset.sectionId || drawer.closest('.shopify-section')?.id.replace('shopify-section-', '') || 'cart-drawer', target: 'cart-drawer' });
      for (const id of ['cart-icon-bubble', 'main-cart-items', 'main-cart-footer', 'cart-notification-product', 'cart-notification-button', 'cart-live-region-text']) {
        const el = domId(id);
        if (el) items.push({ id: el.dataset.id || (id.startsWith('main-cart-') ? el.closest('.shopify-section')?.id.replace('shopify-section-', '') : '') || id, target: '#' + id });
      }
      const seen = new Set(); return items.filter(x => { if (seen.has(x.id)) return false; seen.add(x.id); return true; }).slice(0, 5);
    }
    enqueue(task) {
      this.pending++;
      O.emit('ownstore:cart-pending', { pending: this.pending });
      const next = this.tail.catch(() => {}).then(task);
      this.tail = next.catch(() => {});
      return next.finally(() => { this.pending--; O.emit('ownstore:cart-pending', { pending: this.pending }); });
    }
    read() { return O.request(O.route('cart.js')); }
    async refresh({ open = false } = {}) {
      return this.enqueue(async () => {
        const state = await this.read(), sections = await this.fetchSections(this.sectionRequests());
        this.state = state; this.render(state, sections, open); return state;
      });
    }
    async fetchSections(requests) {
      if (!requests.length) return {};
      const url = O.localURL(location.href); url.searchParams.set('sections', requests.map(x => x.id).join(','));
      return O.request(url);
    }
    async raw(endpoint, payload, requests) {
      const sections = requests.map(x => x.id);
      let body, headers;
      if (payload instanceof FormData) {
        if (sections.length) payload.set('sections', sections.join(','));
        payload.set('sections_url', location.pathname + location.search.replace(/([?&])sections?=[^&]*/g, '$1').replace(/[?&]$/, ''));
        body = payload; headers = { Accept: 'application/json' };
      } else {
        body = JSON.stringify({ ...payload, ...(sections.length ? { sections, sections_url: location.pathname } : {}) });
        headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
      }
      return O.request(O.route('cart/' + endpoint + '.js'), { method: 'POST', body, headers });
    }
    async enforceGiftSafety(state, requests) {
      // A theme cannot make a paid item free. Do not leave a charged "free gift".
      const invalid = (state.items || []).filter(item => {
        const rule = O.json(item.properties?._ownstore_gift || ''); if (!rule) return false;
        const parents = state.items.filter(i => !i.properties?._ownstore_gift && (!rule.parent || i.handle === rule.parent || i.product_handle === rule.parent || i.url?.split('?')[0]?.endsWith('/' + rule.parent)));
        const count = parents.reduce((sum, item) => sum + item.quantity, 0);
        const variantOK = rule.unlockBy !== 'variant' || parents.some(i => (i.variant_options || []).some(v => String(v).toLowerCase() === String(rule.variant).toLowerCase()));
        const eligible = rule.unlockBy === 'cart_subtotal' ? state.total_price >= rule.min : count >= rule.min && variantOK;
        return item.final_line_price > 0 || !eligible;
      });
      for (const item of invalid) {
        if (item.final_line_price > 0) this.warning = text('gift_removed', 'The configured gift was not free in the cart, so it was removed. Your selected products remain in the cart.');
        state = await this.raw('change', { id: item.key, quantity: 0 }, requests);
      }
      return state;
    }
    giftEligible(state, rule) {
      const parents = (state.items || []).filter(i => !i.properties?._ownstore_gift && (!rule.parent || i.handle === rule.parent || i.product_handle === rule.parent || i.url?.split('?')[0]?.endsWith('/' + rule.parent)));
      return rule.unlockBy === 'cart_subtotal' ? parents.reduce((sum, i) => sum + (i.final_line_price || 0), 0) >= rule.min : parents.reduce((sum, i) => sum + i.quantity, 0) >= rule.min;
    }
    async reconcileCartGifts(state, requests) {
      const candidates = [...document.querySelectorAll('cart-drawer-gift')].filter(gift => gift.getRule && /^\d+$/.test(gift.dataset.id || ''));
      const added = new Set();
      for (const gift of candidates) {
        const id = gift.dataset.id, rule = gift.getRule();
        if (this.suppressedGifts.has(id) || added.has(id) || !this.giftEligible(state, rule) || state.items.some(i => idEqual(i.variant_id || i.id, id))) continue;
        added.add(id);
        try {
          await this.raw('add', { items: [{ id, quantity: 1, properties: { _ownstore_gift: JSON.stringify(rule) } }] }, requests);
          state = await this.read();
          state.sections = await this.fetchSections(requests);
          const item = state.items.find(i => idEqual(i.variant_id || i.id, id) && i.properties?._ownstore_gift);
          if (item?.final_line_price > 0) this.suppressedGifts.add(id);
          state = await this.enforceGiftSafety(state, requests);
        } catch (error) { this.warning = text('gift_unconfirmed', 'Your selected products were saved. The optional gift could not be confirmed; please check your cart.'); O.report(error, 'cart-gift'); }
      }
      return state;
    }
    mutate(endpoint, payload, { open = false, source = 'ownstore', validate } = {}) {
      return this.enqueue(async () => {
        this.warning = '';
        const requests = this.sectionRequests();
        const resolved = typeof payload === 'function' ? await payload(this.state) : payload;
        if (!resolved) return this.state;
        let result;
        try {
          result = await this.raw(endpoint, resolved, requests);
          let state = result.item_count !== undefined ? result : await this.read();
          state = await this.enforceGiftSafety(state, requests);
          state = await this.reconcileCartGifts(state, requests);
          let sections = state.sections || result.sections || {};
          const missing = requests.filter(x => typeof sections[x.id] !== 'string');
          if (missing.length) {
            try { sections = { ...sections, ...await this.fetchSections(missing) }; }
            catch (e) { O.report(e, 'cart-section-refresh'); this.warning = text('cart_refresh_failed', 'Your cart was saved, but the display could not refresh. Open the cart page to check it before adding again.'); }
          }
          this.state = state;
          if (endpoint === 'add') this.notificationItems = state.items.filter(item => (result.items || []).some(added => idEqual(added.id || added.variant_id, item.id || item.variant_id) && JSON.stringify(added.properties || {}) === JSON.stringify(item.properties || {}) && idEqual(added.selling_plan_allocation?.selling_plan?.id || '', item.selling_plan_allocation?.selling_plan?.id || '')));
          const info = validate?.(state);
          this.render(state, sections, open, source);
          if (this.warning) this.showMessage(this.warning);
          return Object.assign({}, state, { sections, addedItems: result.items, ownstoreValidation: info });
        } catch (error) {
          O.publish(O.events.cartError, { source, error });
          // No retry: server-side writes may have succeeded despite a lost response.
          if (!error.status) { error.uncertain = true; error.message += ' ' + text('cart_verify_before_retry', 'Please refresh the cart to verify its contents before adding again.'); this.showMessage(error.message); }
          throw error;
        }
      });
    }
    add(items, options = {}) {
      items = aggregate(items);
      const hasFile = items.some(item => Object.values(item.properties || {}).some(value => value instanceof File));
      if (!hasFile) return this.mutate('add', { items }, options);
      const form = new FormData();
      items.forEach((item, i) => { form.append(`items[${i}][id]`, item.id); form.append(`items[${i}][quantity]`, item.quantity); if (item.selling_plan) form.append(`items[${i}][selling_plan]`, item.selling_plan); Object.entries(item.properties || {}).forEach(([key, value]) => form.append(`items[${i}][properties][${key}]`, value)); });
      return this.mutate('add', form, options);
    }
    change(key, quantity, options = {}) {
      const prior = this.state?.items?.find(item => item.key === key);
      if (!quantity && prior?.properties?._ownstore_gift) this.suppressedGifts.add(String(prior.variant_id || prior.id));
      return this.mutate('change', async () => {
        const state = this.state || await this.read();
        let item = state.items.find(item => item.key === key);
        if (!item && prior) item = state.items.find(item => idEqual(item.variant_id || item.id, prior.variant_id || prior.id) && JSON.stringify(item.properties || {}) === JSON.stringify(prior.properties || {}) && idEqual(item.selling_plan_allocation?.selling_plan?.id || '', prior.selling_plan_allocation?.selling_plan?.id || ''));
        if (!item) throw new Error(text('cart_line_changed', 'This cart line changed. Refresh the cart before editing it.'));
        if (!quantity && item.properties?._ownstore_gift) this.suppressedGifts.add(String(item.variant_id || item.id));
        return { id: item.key, quantity: Math.max(0, O.int(quantity)) };
      }, options);
    }
    discount(codes, options = {}) { return this.mutate('update', { discount: [...new Set(codes.map(s => s.trim()).filter(Boolean))].join(',') }, options); }
    note(value) { return this.mutate('update', { note: value }, { source: 'cart-note' }); }
    render(state, sections = {}, open = false, source = 'ownstore') {
      const active = document.activeElement;
      const focus = active?.id || '', selection = active && 'selectionStart' in active ? active.selectionStart : null;
      const drawer = document.querySelector('cart-drawer');
      const wasOpen = drawer?.classList.contains('active');
      for (const request of this.sectionRequests()) {
        const html = sections[request.id]; if (typeof html !== 'string') continue;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        if (request.target === 'cart-drawer') {
          const next = doc.querySelector('cart-drawer');
          if (next && drawer) {
            drawer.innerHTML = next.innerHTML;
            drawer.classList.toggle('is-empty', state.item_count === 0);
          }
        } else {
          const live = document.querySelector(request.target), fresh = doc.querySelector(request.target);
          if (live && fresh) live.innerHTML = fresh.innerHTML;
        }
      }
      document.querySelectorAll('cart-items,cart-drawer-items,cart-drawer,#main-cart-footer').forEach(el => el.classList.toggle('is-empty', !state.item_count));
      const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
      if (bubble) { bubble.hidden = !state.item_count; const count = bubble.querySelector('[aria-hidden="true"]'); if (count) count.textContent = state.item_count < 100 ? state.item_count : ''; }
      O.publish(O.events.cartUpdate, { source, cart: state, cartData: state, sections });
      O.emit('cart:updated', { cart: state, source });
      O.emit('ownstore:cart-updated', { cart: state, source });
      if (drawer && (open || wasOpen)) { drawer.open(); }
      if (focus && !open) {
        const restore = domId(focus); if (restore && !restore.disabled) { restore.focus({ preventScroll: true }); if (selection !== null && typeof restore.setSelectionRange === 'function' && /^(?:text|search|email)$/.test(restore.type)) { try { restore.setSelectionRange(selection, selection); } catch (_) {} } }
      }
      const notification = document.querySelector('cart-notification');
      if (!drawer && open && notification && typeof notification.renderContents === 'function') {
        try { notification.renderContents({ ...state, sections, notificationItems: this.notificationItems || state.items?.slice(0, 1) }); } catch (e) { O.report(e, 'cart-notification'); }
      }
    }
    showMessage(message) {
      const root = document.querySelector('cart-drawer .drawer__inner') || document.querySelector('cart-items') || document.querySelector('main') || document.body;
      visibleError(root, message);
    }
  }
  O.cart = new CartService();
  class QuantityInput extends O.Element {
    init() {
      this.input = this.querySelector('input[type="number"]') || this.querySelector('input'); if (!this.input) return;
      this.on(this, 'click', event => {
        const button = event.target.closest('button'); if (!button || button.disabled) return;
        event.preventDefault();
        this.normalize();
        const delta = button.name === 'minus' || /minus|decrease/.test(button.getAttribute('aria-label') || '') ? -1 : 1;
        const { step, min, max } = this.rules();
        const current = Number(this.input.value), next = O.clamp(current + delta * step, min, max);
        if (next !== current) { this.input.value = next; this.input.dispatchEvent(new Event('change', { bubbles: true })); }
        this.updateButtons();
      });
      this.on(this.input, 'change', () => { this.normalize(); this.updateButtons(); });
      this.validateQtyRules();
    }
    rules() {
      const step = Math.max(1, O.int(this.input.step, 1));
      const min = Math.ceil(Math.max(0, O.int(this.input.min, 1)) / step) * step;
      const rawMax = this.input.max === '' ? Infinity : Number(this.input.max);
      const max = Number.isFinite(rawMax) ? Math.floor(rawMax / step) * step : Infinity;
      return { step, min, max: Math.max(min, max), unavailable: max < min };
    }
    normalize() {
      if (!this.input) return;
      const { step, min, max } = this.rules(), value = Number(this.input.value);
      this.input.value = O.clamp(Number.isFinite(value) ? Math.round(value / step) * step : min, min, max);
    }
    updateButtons() {
      if (!this.input) return;
      const n = Number(this.input.value), { min, max, unavailable } = this.rules();
      this.querySelector('button[name="minus"]')?.toggleAttribute('disabled', unavailable || n <= min);
      this.querySelector('button[name="plus"]')?.toggleAttribute('disabled', unavailable || n >= max);
    }
    validateQtyRules() { this.normalize(); this.updateButtons(); }
  }
  O.register('quantity-input', QuantityInput);
  class ProductInfo extends O.Element {
    init() {
      this.product = O.readProduct(this); this.variants = this.product.variants || [];
      const initialId = this.querySelector('input.product-variant-id')?.value || this.product.selected_variant_id;
      this.variant = this.variants.find(v => idEqual(v.id, initialId)) || this.variants.find(v => v.available) || this.variants[0];
      this.on(this, 'change', event => {
        if (event.target.matches('quantity-input input[name="quantity"]')) {
          const offers = this.querySelector('quantity-breaks');
          if (offers && !offers._updating) offers.selectQuantity?.(O.int(event.target.value, 1));
          if (!offers) this.updateClassicQuantity();
          this.updateTotals();
        }
      });
      this.on(this, 'ownstore:quantity-change', () => this.updateTotals());
      this.listen(O.events.cartUpdate, () => { this.updateClassicQuantity(); this.updateTotals(); });
      this.updateClassicQuantity(); this.updateTotals();
      queueMicrotask(() => { if (this.isConnected) { this.updateClassicQuantity(); this.updateTotals(); } });
    }
    getVariant(id) { return (this.variants || O.variants(this)).find(v => idEqual(v.id, id)); }
    getQuantity() {
      const offers = this.querySelector('quantity-breaks');
      if (offers?.getQuantity) return offers.getQuantity();
      const input = this.querySelector('quantity-input input[name="quantity"]'); return Math.max(1, O.int(input?.value, 1));
    }
    classicQuantityBounds(variant = this.variant) {
      const meta = { ...(this.getVariant(variant?.id) || {}), ...(variant || {}) };
      const rule = meta.quantity_rule || {}, step = Math.max(1, O.int(rule.increment, 1));
      const ruleMin = Math.max(1, O.int(rule.min, 1));
      const inCart = Array.isArray(O.cart.state?.items) ? O.cart.state.items.filter(line => idEqual(line.variant_id || line.id, meta.id)).reduce((n, line) => n + O.int(line.quantity), 0) : O.int(meta.cart_quantity, 0);
      const min = Math.max(step, Math.ceil((ruleMin - inCart) / step) * step);
      let max = rule.max == null ? Infinity : Number(rule.max) - inCart;
      if (meta.inventory_management && meta.inventory_policy !== 'continue' && meta.inventory_quantity != null) max = Math.min(max, Number(meta.inventory_quantity) - inCart);
      if (!variant?.available) max = 0;
      if (Number.isFinite(max)) max = Math.max(0, Math.floor(max / step) * step);
      return { min, max, step, inCart, ruleMin, ruleMax: rule.max, unavailable: max < min };
    }
    assertClassicQuantity(variant, quantity) {
      if (idEqual(this.variant?.id, variant?.id)) variant = this.variant;
      if (!variant?.available) throw new Error(text('soldOut', 'Sold out'));
      const bounds = this.classicQuantityBounds(variant);
      if (bounds.unavailable || !Number.isInteger(quantity) || quantity < bounds.min || quantity > bounds.max || quantity % bounds.step) throw new Error(text('quantity_not_allowed', 'Please select a quantity allowed for this product.'));
    }
    updateClassicQuantity() {
      // Cart controls and package selectors have separate quantity contracts.
      if (this.querySelector('quantity-breaks')) return;
      const bounds = this.classicQuantityBounds();
      this.querySelectorAll('quantity-input input[name="quantity"]').forEach(input => {
        if (input.closest('main-product-upsell,cart-drawer-upsell,quantity-break-upsells,quantity-gifts')) return;
        input.dataset.cartQuantity = bounds.inCart;
        input.dataset.min = bounds.ruleMin;
        if (bounds.ruleMax != null) input.dataset.max = bounds.ruleMax; else delete input.dataset.max;
        input.min = bounds.min; input.step = bounds.step;
        if (Number.isFinite(bounds.max)) input.max = bounds.max; else input.removeAttribute('max');
        input.disabled = bounds.unavailable;
        input.setCustomValidity(bounds.unavailable ? text('unavailable', 'Unavailable') : '');
        input.closest('quantity-input')?.validateQtyRules?.();
      });
      const quantity = this.getQuantity();
      let message = '';
      try { this.assertClassicQuantity(this.variant, quantity); } catch (error) { message = error.message; }
      this.querySelectorAll('product-form').forEach(form => {
        if (!form.closest('main-product-upsell,cart-drawer-upsell,quantity-break-upsells,quantity-gifts')) form.toggleSubmitButton?.(Boolean(message), message || null);
      });
      (this.closest('.shopify-section') || this).querySelectorAll('sticky-atc .sticky-atc__add_button').forEach(button => { const disabled = Boolean(message || button.form?.closest('product-form')?.busy); button.disabled = disabled; button.setAttribute('aria-disabled', String(disabled)); });
    }
    setVariant(variant, { source = null, updateURL = true, render = true } = {}) {
      this.variant = variant || null;
      const section = this.dataset.section;
      this.querySelectorAll('input[name="id"],input.product-variant-id').forEach(input => { if (input.closest('main-product-upsell,cart-drawer-upsell,quantity-break-upsells,quantity-gifts')) return; input.value = variant?.id || ''; input.disabled = !variant; });
      const root = this.closest('.shopify-section') || this;
      root.querySelectorAll('variant-selects,variant-radios,secondary-variant-select,secondary-variant-select-separate').forEach(picker => { if (picker !== source) picker.setVariant?.(variant, false); });
      if (source?.localName === 'variant-selects' || source?.localName === 'variant-radios' || source?.localName?.startsWith('secondary-variant-select')) this.querySelector('quantity-breaks')?.setMainVariant?.(variant);
      this.querySelector('shrine-subscription-widget')?.variantChanged?.(variant);
      const ready = !!variant?.available;
      this.querySelectorAll('product-form').forEach(form => { if (!form.closest('main-product-upsell,quantity-gifts,quantity-break-upsells')) form.toggleSubmitButton?.(!ready, ready ? null : !variant ? text('unavailable', 'Unavailable') : text('soldOut', 'Sold out')); });
      root.querySelectorAll('sticky-atc .sticky-atc__add_button').forEach(btn => { btn.disabled = !ready || Boolean(btn.form?.closest('product-form')?.busy); const label = btn.querySelector('.sticky-atc__label__text'); if (label) label.textContent = ready ? btn.dataset.label || text('addToCart', 'Add to cart') : !variant ? text('unavailable', 'Unavailable') : text('soldOut', 'Sold out'); });
      if (variant) {
        if (updateURL && this.dataset.updateUrl !== 'false' && this.classList.contains('main-product__info-container')) { const url = new URL(location.href); url.searchParams.set('variant', variant.id); history.replaceState({}, '', url); }
        this.updateMedia(variant);
        if (variant.available) this.querySelector('pickup-availability')?.fetchAvailability?.(variant.id); else this.querySelector('pickup-availability')?.removeAttribute('available');
        this.initShareLinks();
        if (render) this.renderVariant(variant);
      }
      this.updateTotals();
      this.updateClassicQuantity();
      this.querySelector('quantity-breaks')?.updateValidity?.();
      O.publish(O.events.variantChange, { data: { sectionId: section, variant }, sectionId: section, variant });
      O.emit('ownstore:variant-change', { variant, sectionId: section }, this);
    }
    initShareLinks() { const url = new URL(this.dataset.url || location.pathname, location.origin); if (this.variant) url.searchParams.set('variant', this.variant.id); this.querySelectorAll('share-button').forEach(button => button.updateUrl?.(url.href)); }
    updateMedia(variant) {
      const section = this.dataset.section, mediaId = variant?.featured_media?.id || variant?.featured_image?.id;
      const gallery = domId('MediaGallery-' + section);
      if (gallery) { const values = O.options(variant); const fullVariant = { ...variant, option1: values[0], option2: values[1], option3: values[2] }; gallery.setActiveMedia?.(section + '-' + mediaId, true, Boolean(gallery.dataset.filteringOption), fullVariant); }
      const img = domId('sticky-atc-image-' + section), src = variant?.featured_image?.src;
      if (img && src) { img.src = src; img.removeAttribute('srcset'); }
      if (gallery?.dataset.variantImageFiltering && gallery.dataset.variantImageFiltering !== 'none') this.filterMedia(gallery, variant);
    }
    filterMedia(gallery, variant) {
      const values = O.options(variant).map(v => String(v).toLowerCase());
      gallery.querySelectorAll('[data-variant-filter],[data-variant-options]').forEach(media => {
        const filter = (media.dataset.variantFilter || media.dataset.variantOptions || '').toLowerCase();
        if (!filter) return; media.hidden = !values.some(v => filter.includes(v));
      });
      gallery.querySelectorAll('slider-component').forEach(slider => slider.resetPages?.());
    }
    async renderVariant(variant) {
      if (!this.dataset.url) return;
      this.variantAbort?.abort(); this.variantAbort = new AbortController();
      const url = O.localURL(this.dataset.url); url.searchParams.set('variant', variant.id); url.searchParams.set('section_id', this.dataset.originalSection || this.dataset.section);
      try {
        const html = await O.request(url, { asText: true, signal: this.variantAbort.signal });
        if (!this.isConnected || !idEqual(this.variant?.id, variant.id)) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        for (const prefix of ['price-', 'Price-', 'Inventory-', 'Sku-', 'SKU-', 'Volume-', 'QuantityRules-']) {
          const id = prefix + this.dataset.section, current = domId(id), next = doc.getElementById(prefix + (this.dataset.originalSection || this.dataset.section));
          if (current && next && this.contains(current)) { current.innerHTML = this.dataset.originalSection ? next.innerHTML.replaceAll(this.dataset.originalSection, this.dataset.section) : next.innerHTML; current.className = next.className; }
        }
        this.updateTotals();
      } catch (e) { if (e.name !== 'AbortError') O.report(e, 'variant-section'); }
    }
    updateTotals() {
      if (!this.variant) return;
      const offers = this.querySelector('quantity-breaks'), subscription = this.querySelector('shrine-subscription-widget');
      let totals = offers?.getTotals?.() || { price: this.variant.price * this.getQuantity(), compare: Math.max(this.variant.price, this.variant.compare_at_price || 0) * this.getQuantity(), quantity: this.getQuantity() };
      if (subscription?.isSubscribed?.()) totals = subscription.applyToTotals(totals, this.variant);
      this.totals = totals;
      const section = this.dataset.section;
      (this.closest('.shopify-section') || this).querySelectorAll('.main-atc-price-span-' + CSS.escape(section)).forEach(el => { el.textContent = O.money(totals.price); });
      if (O.bool(offers?.dataset.updatePdpPrices) || O.bool(subscription?.dataset.updatePrices)) {
        const container = domId('price-' + section) || this.querySelector('.price');
        const sale = container?.querySelector('.price-item--sale'), regular = container?.querySelector('.price__regular .price-item--regular'), compare = container?.querySelector('.price__sale .price-item--regular');
        if (sale) sale.textContent = O.money(totals.price); if (regular) regular.textContent = O.money(totals.price); if (compare) compare.textContent = O.money(totals.compare);
        container?.querySelector('.price')?.classList.toggle('price--on-sale', totals.compare > totals.price);
      }
      this.querySelectorAll('quantity-gifts').forEach(gifts => gifts.update?.());
      O.emit('ownstore:totals-change', totals, this);
    }
    destroy() { this.variantAbort?.abort(); }
  }
  O.register('product-info', ProductInfo);
  class VariantSelects extends O.Element {
    init() {
      this.variants = O.variants(this); this.on(this, 'change', event => this.onVariantChange(event));
      if (this.querySelector('.product-form__input')) {
        const before = O.selectVariant(this.variants, O.selectedOptions(this));
        const variant = O.updateOptions(this, this.variants, O.bool(this.dataset.skipUnavailable));
        this.currentVariant = variant; this.updateLabels();
        if (variant && !idEqual(before?.id, variant.id)) O.owner(this)?.setVariant?.(variant, { source: this, updateURL: this.dataset.updateUrl !== 'false' });
      }
    }
    onVariantChange() {
      const variant = O.updateOptions(this, this.variants, O.bool(this.dataset.skipUnavailable));
      this.currentVariant = variant;
      this.updateLabels();
      O.owner(this)?.setVariant?.(variant, { source: this, updateURL: this.dataset.updateUrl !== 'false' });
    }
    setVariant(variant) { if (!variant) return; this.currentVariant = variant; O.setOptionValues(this, O.options(variant)); O.updateOptions(this, this.variants, O.bool(this.dataset.skipUnavailable), false); this.updateLabels(); }
    getVariantData() { return this.variants; }
    updateOptions() { return O.selectedOptions(this); }
    updateMasterId() { this.currentVariant = O.selectVariant(this.variants, O.selectedOptions(this)); }
    updateLabels() {
      [...this.querySelectorAll('.product-form__input')].forEach(group => { const selected = group.querySelector('select')?.value ?? group.querySelector('input:checked')?.value; group.querySelectorAll('[data-selected-value], .selected-value').forEach(el => { el.textContent = selected || ''; }); });
      this.updateOptionPresentation();
    }
    updateOptionPresentation() {
      const selected = O.selectedOptions(this);
      [...this.querySelectorAll('.product-form__input')].forEach((group, index) => {
        group.querySelectorAll('input[type="radio"]').forEach(input => {
          const candidates = this.variants.filter(variant => O.options(variant).every((value, position) => position < index ? String(value) === String(selected[position]) : position === index ? String(value) === input.value : true));
          const variant = candidates.find(v => O.options(v).every((value, position) => String(value) === String(selected[position]))) || candidates.find(v => v.available) || candidates[0];
          const label = group.querySelector('label[for="' + CSS.escape(input.id) + '"]');
          const surface = input.closest('.color-swatch') || label;
          if (!surface || !variant) return;
          const picture = variant.featured_image || variant.image, src = typeof picture === 'string' ? picture : picture?.src;
          surface.querySelectorAll('[data-variant-swatch-image],[data-variant-break-image]').forEach(img => { img.hidden = !src; if (src) { img.src = src; img.removeAttribute('srcset'); } });
          surface.querySelectorAll('[data-variant-swatch-alt]').forEach(el => { el.style.setProperty('--bg-color', picture?.alt || 'transparent'); });
          const price = Number(variant.price) || 0, compare = Math.max(price, Number(variant.compare_at_price) || 0);
          surface.querySelectorAll('[data-variant-template]').forEach(el => { O.html(el, O.priceText(el.dataset.variantTemplate, { name: input.value, price, compare })); });
          surface.querySelectorAll('.quantity-break__compare-price').forEach(el => { el.hidden = compare <= price; });
        });
      });
    }
  }
  O.register('variant-selects', VariantSelects);
  O.register('variant-radios', class extends VariantSelects {});
  class SecondaryVariant extends VariantSelects {
    init() {
      if (!this.querySelector('option[data-variant-id]')) { super.init(); return; }
      this.variants = O.variants(this);
      this.on(this, 'change', () => this.onVariantChange());
      const selected = this.querySelector('select')?.selectedOptions[0];
      const variant = O.owner(this)?.variant || this.variants.find(item => idEqual(item.id, selected?.dataset.variantId));
      if (variant) this.setVariant(variant);
    }
    onVariantChange() {
      const option = this.querySelector('select')?.selectedOptions[0];
      const combinedId = option?.dataset.variantId;
      const variant = combinedId ? this.variants.find(v => idEqual(v.id, combinedId)) : O.updateOptions(this, this.variants, O.bool(this.dataset.skipUnavailable));
      O.owner(this)?.setVariant?.(variant, { source: this });
    }
    setVariant(variant) { if (!variant) return; this.currentVariant = variant; const combined = this.querySelector('option[data-variant-id="' + variant.id + '"]'); if (combined) combined.selected = true; else super.setVariant(variant); }
  }
  O.register('secondary-variant-select', SecondaryVariant);
  O.register('secondary-variant-select-separate', class extends SecondaryVariant {});
  class CardVariant extends VariantSelects {
    onVariantChange() {
      const variant = O.updateOptions(this, this.variants, O.bool(this.dataset.skipUnavailable));
      const card = this.closest('.card-wrapper') || this.parentElement;
      card.querySelectorAll('input[name="id"]').forEach(input => { input.value = variant?.id || ''; });
      card.querySelectorAll('product-form').forEach(form => form.toggleSubmitButton?.(!variant?.available, variant?.available ? null : text('soldOut', 'Sold out')));
      card.querySelectorAll('a[href*="/products/"]').forEach(link => { const url = O.localURL(link.href); if (variant) url.searchParams.set('variant', variant.id); link.href = url; });
      const img = card.querySelector('.card__media img'); if (img && variant?.featured_image?.src) { img.src = variant.featured_image.src; img.removeAttribute('srcset'); }
      card.querySelectorAll('.price-item--sale,.price__regular .price-item--regular').forEach(price => { if (variant) price.textContent = O.money(variant.price); });
    }
  }
  O.register('card-variant-selects', CardVariant);

  class QuantityBreaks extends O.Element {
    init() {
      this.variants = O.variants(this); if (!this.variants.length) this.variants = O.variants(scopeRoot(this));
      this.rules = new Map();
      try { JSON.parse(this.querySelector('[data-quantity-rules]')?.textContent || '[]').forEach(rule => this.rules.set(String(rule.id), rule)); } catch (error) { O.report?.(error, 'quantity-rules'); }
      this.radios = [...this.querySelectorAll('input[type="radio"][name="quantity"]')];
      this.cards = [...this.querySelectorAll('.quantity-break[data-quantity]')];
      this.saved = [];
      this.on(this, 'change', e => {
        if (this.radios.includes(e.target)) this.select(e.target);
        else if (e.target.closest('.quantity-break__selector-item')) {
          const row = e.target.closest('.quantity-break__selector-item');
          const v = this.updateRow(row);
          this.saveRows(); this.updatePrices();
          if (v && !O.bool(this.dataset.preventGalleryScroll)) O.owner(this)?.updateMedia?.(v);
          O.emit('ownstore:quantity-change', { quantity: this.getQuantity() }, this);
        }
      });
      this.listen(O.events.cartUpdate, () => this.updateValidity());
      this.cards.forEach(card => this.rows(card).forEach(row => this.updateRow(row)));
      const first = this.dataset.preselected === 'none' ? null : this.radios.find(r => r.checked) || this.radios[0];
      if (first) this.select(first, false);
      else { this.radios.forEach(r => { r.checked = false; }); this.syncSelection(); this.updatePrices(); }
      // Product forms later in the document finish initialization in this turn.
      queueMicrotask(() => { if (this.isConnected) this.updateValidity(); });
    }
    cardFor(radio) {
      if (!radio) return null;
      return (this.cards || []).find(c => c.htmlFor === radio.id || c.dataset.input === radio.dataset.input) || (this.cards || [])[(this.radios || []).indexOf(radio)];
    }
    get activeCard() { return this.cardFor(this.radios?.find(r => r.checked)); }
    getQuantity() { return Math.max(1, O.int(this.radios?.find(r => r.checked)?.value, 1)); }
    rows(card = this.activeCard) { return [...(card?.querySelectorAll('.quantity-break__selector-item') || [])]; }
    updateRow(row) {
      const v = O.updateOptions(row, this.variants, O.bool(this.dataset.skipUnavailable));
      row.dataset.selectedId = v?.id || ''; row.dataset.available = String(Boolean(v?.available));
      const img = row.querySelector('.quantity-break__selector-item__image img');
      if (img) {
        if (img.dataset.fallbackSrc === undefined) img.dataset.fallbackSrc = img.getAttribute('src') || '';
        const src = v?.featured_image?.src || img.dataset.fallbackSrc;
        if (src) { img.src = src; img.removeAttribute('srcset'); } else img.removeAttribute('src');
        img.alt = v?.featured_image?.alt || v?.public_title || v?.title || img.alt;
      }
      return v;
    }
    saveRows() { this.rows().forEach((row, i) => { this.saved[i] = row.dataset.selectedId; }); }
    syncSelection() {
      this.cards.forEach(card => {
        const chosen = card === this.activeCard;
        card.dataset.selected = String(chosen); card.classList.toggle('quantity-break--selected', chosen);
        const variants = card.querySelector('.quantity-break__variants');
        if (variants) { variants.setAttribute('aria-hidden', String(!chosen)); variants.inert = !chosen; }
      });
      this.radios.forEach(r => r.setAttribute('aria-expanded', String(r.checked)));
      this.querySelectorAll('quantity-break-upsells').forEach(el => el.update?.());
    }
    select(radio, preserve = true) {
      if (!radio || radio.disabled) return;
      const prior = this._active;
      if (preserve && prior && prior !== radio) {
        this.rows(this.cardFor(prior)).forEach((row, i) => { this.saved[i] = row.dataset.selectedId; });
      }
      this.radios.forEach(r => { r.checked = r === radio; }); this._active = radio;
      this.syncSelection();
      this.rows().forEach((row, i) => {
        const v = this.variants.find(v => idEqual(v.id, this.saved[i]));
        if (v) O.setOptionValues(row, O.options(v)); this.updateRow(row);
      });
      this.saveRows();
      this._updating = true;
      O.owner(this)?.querySelectorAll('quantity-input input[name="quantity"]').forEach(input => { input.value = this.getQuantity(); input.closest('quantity-input')?.updateButtons?.(); });
      this._updating = false;
      this.updatePrices();
      O.emit('ownstore:quantity-change', { quantity: this.getQuantity() }, this);
    }
    selectQuantity(quantity) {
      const enabled = this.radios.filter(r => !r.disabled), exact = enabled.find(r => Number(r.value) === quantity);
      if (exact) { this.select(exact); return; }
      const current = this.getQuantity(), sorted = [...enabled].sort((a, b) => Number(a.value) - Number(b.value));
      const nearest = quantity > current ? sorted.find(r => Number(r.value) >= quantity) || sorted.at(-1) : sorted.filter(r => Number(r.value) <= quantity).at(-1) || sorted[0];
      if (nearest) this.select(nearest);
    }
    setMainVariant(variant) {
      this.mainVariant = variant || null; this.hasMainVariant = true;
      this.saved = [];
      if (variant) this.cards.forEach(card => this.rows(card).forEach(row => { O.setOptionValues(row, O.options(variant)); this.updateRow(row); }));
      this.saveRows(); this.updatePrices();
    }
    currentVariant() { return this.hasMainVariant ? this.mainVariant : O.owner(this)?.variant || this.variants.find(v => v.available) || this.variants[0]; }
    assertQuantity(items) {
      for (const item of items) {
        const variant = this.variants.find(v => idEqual(v.id, item.id));
        if (!variant?.available) throw new Error(text('soldOut', 'Sold out'));
        const meta = this.rules?.get(String(item.id)) || variant;
        const inCart = Array.isArray(O.cart.state?.items) ? O.cart.state.items.filter(line => idEqual(line.variant_id || line.id, item.id)).reduce((n, line) => n + O.int(line.quantity), 0) : O.int(meta.cart_quantity, 0);
        const total = inCart + item.quantity, rule = meta.quantity_rule || variant.quantity_rule || {};
        const min = Math.max(1, O.int(rule.min, 1)), step = Math.max(1, O.int(rule.increment, 1));
        const exceedsRule = rule.max != null && total > Number(rule.max);
        const stock = meta.inventory_quantity;
        const exceedsStock = meta.inventory_management && meta.inventory_policy !== 'continue' && stock != null && total > Number(stock);
        if (total < min || (total - min) % step !== 0 || exceedsRule || exceedsStock) throw new Error(this.dataset.unavailableText || text('quantity_unavailable', 'This quantity is unavailable for the selected options.'));
      }
      return items;
    }
    getItems(card = this.activeCard) {
      if (!card) throw new Error(this.dataset.selectionRequiredText || text('choose_offer', 'Choose an offer'));
      if (this.hasMainVariant && !this.mainVariant?.available) throw new Error(text('soldOut', 'Sold out'));
      const count = Math.max(1, O.int(card.dataset.quantity, 1)), rows = this.rows(card);
      if (!rows.length) {
        const variant = this.currentVariant();
        if (!variant?.available) throw new Error(text('soldOut', 'Sold out'));
        return this.assertQuantity([{ id: variant.id, quantity: count }]);
      }
      const items = rows.slice(0, count).map(row => {
        const variant = this.variants.find(v => idEqual(v.id, row.dataset.selectedId));
        if (!variant?.available) throw new Error(this.dataset.unavailableText || text('every_item_options_required', 'Please choose available options for every item.'));
        return { id: variant.id, quantity: 1 };
      });
      if (items.length !== count) throw new Error(this.dataset.unavailableText || text('package_options_incomplete', 'The package options are incomplete.'));
      return this.assertQuantity(aggregate(items));
    }
    getTotals(card = this.activeCard) {
      const quantity = Math.max(1, O.int(card?.dataset.quantity, 1)), rows = this.rows(card);
      const fallback = this.currentVariant() || this.variants?.[0] || { price: O.int(this.dataset.basePrice), compare_at_price: 0 };
      const chosen = rows.length ? rows.slice(0, quantity).map(row => this.variants.find(v => idEqual(v.id, row.dataset.selectedId)) || fallback) : Array(quantity).fill(fallback);
      const base = chosen.reduce((n, v) => n + Number(v.price), 0);
      const compare = chosen.reduce((n, v) => n + (card?.dataset.compareMode === 'compare_price' ? Math.max(v.price, v.compare_at_price || 0) : v.price), 0);
      const configuredMultiplier = Number(card?.dataset.percentageLeft ?? 1);
      const multiplier = Number.isFinite(configuredMultiplier) ? O.clamp(configuredMultiplier, 0, 1) : 1;
      return { price: Math.max(0, Math.round(base * multiplier - Math.max(0, O.int(card?.dataset.fixedDiscount)))), compare: Math.max(compare, base), quantity, base, variants: chosen };
    }
    updatePrices() {
      for (const card of this.cards || []) {
        let totals = this.getTotals(card);
        const subscription = O.owner(this)?.querySelector('shrine-subscription-widget');
        if (O.bool(subscription?.dataset.updatePrices) && subscription?.isSubscribed?.()) totals = subscription.applyToTotals(totals);
        if (this.dataset.updatePrices !== 'false') {
          card.querySelectorAll('.variant-price-update[data-text]').forEach(el => O.html(el, O.priceText(el.dataset.text, totals, this.dataset.moneyFormat)));
          card.querySelectorAll('.quantity-break__compare-price').forEach(el => { const hidden = totals.compare <= totals.price; el.classList.toggle('hidden', hidden); el.hidden = hidden; });
        }
      }
      this.updateValidity(); O.owner(this)?.updateTotals?.();
    }
    updateValidity() {
      let message = '';
      for (const card of this.cards || []) {
        let available = true;
        try { this.getItems(card); } catch (_) { available = false; }
        card.dataset.available = String(available);
      }
      try { this.getItems(); } catch (error) { message = error.message; }
      this.dataset.available = String(!message);
      const status = this.querySelector('[data-quantity-status]');
      if (status) { status.textContent = message; status.hidden = !message; }
      const form = O.owner(this)?.querySelector('product-form[data-main="true"],product-form.main-product-form');
      form?.toggleSubmitButton?.(Boolean(message), message || null);
    }
  }
  O.register('quantity-breaks', QuantityBreaks);

  class QuantityBreakUpsells extends O.Element {
    init() {
      this.on(this, 'click', e => {
        const item = e.target.closest('.quantity-break__upsell');
        if (!item || !O.bool(item.dataset.toggle) || e.target.closest('a,select,option')) return;
        e.preventDefault(); this.setSelected(item, !O.bool(item.dataset.selected));
      });
      this.on(this, 'keydown', e => { if (['Enter', ' '].includes(e.key) && e.target.matches('.quantity-break__upsell[data-toggle="true"]')) { e.preventDefault(); e.target.click(); } });
      this.querySelectorAll('.quantity-break__upsell[data-toggle="true"]').forEach(item => {
        const key = this.storageKey(item);
        const saved = this.dataset.saveLocalStorage === 'true' && key ? O.storage.get(key) : null;
        if (typeof saved === 'boolean') item.dataset.selected = String(saved);
      });
      this.update();
    }
    storageKey(item) {
      const parent = this.dataset.mainProduct, upsell = item.dataset.handle || item.dataset.product;
      return parent && upsell ? 'quantity-upsell:' + parent + ':' + upsell : null;
    }
    setSelected(item, selected) {
      const key = this.storageKey(item), offers = this.closest('quantity-breaks');
      item.dataset.selected = String(selected);
      if (key && this.dataset.saveLocalStorage === 'true') O.storage.set(key, Boolean(selected));
      // Every offer represents the same optional add-on preference for this product.
      (offers?.querySelectorAll('quantity-break-upsells') || [this]).forEach(container => {
        container.querySelectorAll('.quantity-break__upsell[data-toggle="true"]').forEach(other => {
          if (other === item || key && container.storageKey(other) === key) other.dataset.selected = String(selected);
        });
        container.update();
      });
    }
    update() {
      const offers = this.closest('quantity-breaks'), chosen = this.closest('.quantity-break') === offers?.activeCard;
      this.querySelectorAll('.quantity-break__upsell').forEach(item => {
        if (O.bool(item.dataset.toggle)) { item.tabIndex = chosen ? 0 : -1; item.setAttribute('role', 'checkbox'); item.setAttribute('aria-checked', item.dataset.selected || 'false'); }
        item.classList.toggle('quantity-break__upsell--selected', O.bool(item.dataset.selected));
      });
    }
    getItems() {
      const offers = this.closest('quantity-breaks'); if (this.closest('.quantity-break') !== offers?.activeCard) return [];
      return [...this.querySelectorAll('.quantity-break__upsell')].filter(item => O.bool(item.dataset.selected)).map(item => {
        const gift = item.dataset.type === 'gift';
        return { id: item.dataset.product, quantity: 1, properties: gift ? { _ownstore_gift: JSON.stringify({ parent: this.dataset.mainProduct, min: O.int(item.dataset.minQuantity, offers.getQuantity()), unlockBy: 'quantity' }) } : { _ownstore_upsell: this.dataset.mainProduct || 'quantity-offer' } };
      });
    }
  }
  O.register('quantity-break-upsells', QuantityBreakUpsells);

  class QuantityGifts extends O.Element {
    init() { this.update(); this.on(scopeRoot(this), 'ownstore:variant-change', () => this.update()); this.on(scopeRoot(this), 'ownstore:quantity-change', () => this.update()); }
    update() {
      const owner = O.owner(this), quantity = owner?.getQuantity?.() || 1, options = O.options(owner?.variant || {}).map(v => String(v).toLowerCase());
      this.querySelectorAll('.quantity-gift').forEach(gift => {
        const unlocked = this.dataset.unlockBy === 'variant' ? options.includes(String(gift.dataset.variant).toLowerCase()) : quantity >= O.int(gift.dataset.quantity, 1);
        gift.dataset.unlocked = String(unlocked); gift.classList.toggle('quantity-gift--unlocked', unlocked); gift.setAttribute('aria-disabled', String(!unlocked));
      });
    }
    getItems() {
      this.update();
      return [...this.querySelectorAll('.quantity-gift[data-unlocked="true"]')].filter(g => /^\d+$/.test(g.dataset.product || '')).map(g => ({ id: g.dataset.product, quantity: 1, properties: { _ownstore_gift: JSON.stringify({ parent: this.dataset.mainProduct, min: this.dataset.unlockBy === 'variant' ? 1 : O.int(g.dataset.quantity, 1), unlockBy: this.dataset.unlockBy || 'quantity', variant: g.dataset.variant }) } }));
    }
  }
  O.register('quantity-gifts', QuantityGifts);

  class CustomField extends O.Element {
    init() {
      this.inputs = [...this.querySelectorAll('input,textarea,select')];
      for (const input of this.inputs) { if (!input.name || input.type !== 'radio') input.name = 'properties[' + this.dataset.name + ']'; input.setAttribute('aria-required', String(O.bool(this.dataset.required))); }
      this.on(this, 'input', () => this.validate(false)); this.on(this, 'change', () => this.validate(false));
      this.validate(false);
    }
    get value() { const inputs = this.inputs || [...this.querySelectorAll('input,textarea,select')]; const input = inputs.find(el => el.type === 'radio' ? el.checked : true); return input?.type === 'file' ? input.files?.[0] || '' : input?.value ?? this.dataset.defaultValue ?? ''; }
    validate(show = true) {
      const value = this.value, required = O.bool(this.dataset.required);
      const input = (this.inputs || []).find(el => el.type !== 'radio' || el.checked);
      const valid = (!required || String(value || '').trim().length > 0) && (!input || input.validity.valid);
      this.dataset.valid = String(valid); this.classList.toggle('custom-product-field--error', show && !valid);
      if (input) input.setAttribute('aria-invalid', String(show && !valid));
      const error = this.querySelector('.input-error-msg'); if (error) error.hidden = !show || valid;
      return valid;
    }
    property() { return this.value === '' ? {} : { [this.dataset.name]: this.value }; }
  }
  O.register('custom-product-field', CustomField);

  class SubscriptionWidget extends O.Element {
    init() {
      this.subscribed = this.dataset.preselected !== 'one_time';
      this.on(this, 'click', e => {
        const box = e.target.closest('.subscription__box[data-type]'); if (!box || e.target.closest('select,option,input,a')) return;
        this.subscribed = O.bool(this.dataset.isCheckbox) ? !this.subscribed : box.dataset.type !== 'one_time'; this.update();
      });
      this.on(this, 'keydown', e => { if ([' ', 'Enter'].includes(e.key) && e.target.matches('.subscription__box')) { e.preventDefault(); e.target.click(); } });
      this.on(this, 'change', e => { if (e.target.matches('select,input')) { this.subscribed = true; this.update(); } });
      this.on(scopeRoot(this), 'ownstore:quantity-change', () => this.renderPrices());
      this.update();
    }
    isSubscribed() { return Boolean(this.subscribed); }
    getPlanId() { if (!this.isSubscribed()) return ''; const option = this.querySelector('select option:checked, input[data-selling-plan-id]:checked'); return option?.dataset.sellingPlanId || this.dataset.firstSellingPlanId || ''; }
    allocation(variant) { const id = this.getPlanId(); return variant?.selling_plan_allocations?.find(a => idEqual(a.selling_plan?.id ?? a.selling_plan_id, id)); }
    validateVariants(variants) {
      if (!this.isSubscribed()) { if (O.owner(this)?.product?.requires_selling_plan) throw new Error(text('subscription_required', 'This product requires a subscription plan.')); return; }
      if (!this.getPlanId()) throw new Error(text('subscription_frequency_required', 'Please select a subscription frequency.'));
      for (const variant of variants) if (!this.allocation(variant)) throw new Error(text('subscription_package_unavailable', 'The selected subscription is not available for every item in this package. Please change the options or frequency.'));
    }
    applyToTotals(totals, variant) {
      if (!this.isSubscribed()) return totals;
      const variants = totals.variants || Array(totals.quantity || 1).fill(variant);
      const allocations = variants.map(v => this.allocation(v)); if (allocations.some(a => !a)) return totals;
      const price = allocations.reduce((n, a) => n + Number(a.price), 0);
      // A selling-plan price is already calculated by Shopify; do not apply it twice.
      return { ...totals, price, compare: variants.reduce((n, v, i) => n + Math.max(v.price, allocations[i].compare_at_price || 0, v.compare_at_price || 0), 0) };
    }
    variantChanged() { this.update(); }
    renderPrices() {
      const owner = O.owner(this), variant = owner?.variant; if (!variant) return;
      const offers = owner.querySelector('quantity-breaks'), mode = this.dataset.displayedPrices || 'unit';
      const packageTotals = offers?.getTotals?.();
      const quantity = mode === 'unit' ? 1 : Math.max(1, packageTotals?.quantity || owner.getQuantity());
      const variants = mode === 'unit' ? [variant] : packageTotals?.variants || Array(quantity).fill(variant);
      const base = variants.reduce((n, v) => n + v.price, 0);
      const comparison = variants.reduce((n, v) => n + Math.max(v.price, v.compare_at_price || 0), 0);
      const oneTimePrice = mode !== 'unit' && packageTotals ? packageTotals.price : base;
      const divisor = mode === 'unit_with_discount' ? quantity : 1;
      const totals = { price: oneTimePrice / divisor, compare: comparison / divisor, quantity: mode === 'total' ? quantity : 1, subscriptionName: this.dataset.subscriptionName };
      const planId = this.getPlanId() || this.dataset.firstSellingPlanId;
      const allocations = variants.map(v => v.selling_plan_allocations?.find(a => idEqual(a.selling_plan?.id ?? a.selling_plan_id, planId)));
      this.querySelectorAll('.one-time-price-update[data-text]').forEach(el => O.html(el, O.priceText(el.dataset.text, totals, this.dataset.moneyFormat)));
      if (allocations.every(Boolean)) {
        const price = allocations.reduce((n, a) => n + Number(a.price), 0);
        const compare = allocations.reduce((n, a, i) => n + Math.max(Number(a.compare_at_price) || variants[i].price, this.dataset.compareBasedOn === 'compare' ? variants[i].compare_at_price || 0 : 0), 0);
        this.querySelectorAll('.sub-price-update[data-text]').forEach(el => O.html(el, O.priceText(el.dataset.text, { ...totals, price: price / divisor, compare: compare / divisor }, this.dataset.moneyFormat)));
      }
    }
    update() {
      const owner = O.owner(this);
      this.querySelectorAll('.subscription__box[data-type]').forEach(box => {
        const chosen = (box.dataset.type === 'one_time') !== this.isSubscribed(); box.dataset.selected = String(chosen); box.classList.toggle('subscription__box--selected', chosen); box.setAttribute('role', O.bool(this.dataset.isCheckbox) ? 'checkbox' : 'radio'); box.setAttribute('aria-checked', String(chosen)); box.tabIndex = 0;
      });
      this.renderPrices();
      if (O.bool(this.dataset.updatePrices)) owner?.querySelector('quantity-breaks')?.updatePrices?.();
      owner?.querySelectorAll('product-form[data-main="true"] form').forEach(form => { let input = form.querySelector('input[name="selling_plan"]'); if (!input) { input = document.createElement('input'); input.type = 'hidden'; input.name = 'selling_plan'; form.append(input); } input.value = this.getPlanId(); input.disabled = !input.value; });
      owner?.updateTotals?.(); O.emit('ownstore:subscription-change', { sellingPlan: this.getPlanId() }, this);
    }
  }
  O.register('shrine-subscription-widget', SubscriptionWidget);

  class BundleDeals extends O.Element {
    init() {
      this.on(this, 'change', e => { if (e.target.matches('.bundle-deals__checkbox-js[data-required="true"]')) e.target.checked = true; const group = e.target.closest('.bundle-deals__variant-selects-js'); if (group) this.updateVariant(group); this.update(); });
      this.querySelectorAll('.bundle-deals__variant-selects-js').forEach(el => this.updateVariant(el)); this.update();
    }
    updateVariant(group) {
      const v = O.updateOptions(group, O.variants(group), O.bool(this.dataset.skipUnavailable));
      const row = group.closest('.bundle-deals__product-js'), box = row?.querySelector('.bundle-deals__checkbox-js');
      if (box) { box.dataset.id = v?.id || ''; box.dataset.price = v?.price || 0; box.dataset.comparePrice = Math.max(v?.price || 0, v?.compare_at_price || 0); box.dataset.available = String(Boolean(v?.available)); }
      const image = this.querySelectorAll('.bundle-deals__media-item-img-js')[O.int(group.dataset.index)];
      if (image && v?.featured_image?.src) { image.src = v.featured_image.src; image.removeAttribute('srcset'); }
    }
    getItems() {
      this.querySelectorAll('.bundle-deals__checkbox-js[data-required="true"]').forEach(el => { el.checked = true; });
      const boxes = [...this.querySelectorAll('.bundle-deals__checkbox-js')].filter(el => el.checked);
      if (!boxes.length) throw new Error(text('bundle_selection_required', 'Select at least one product for your bundle.'));
      return boxes.map(el => { if (el.dataset.available === 'false' || !el.dataset.id) throw new Error(text('bundle_product_unavailable', 'A selected bundle product is unavailable.')); return { id: el.dataset.id, quantity: 1 }; });
    }
    update() {
      let base = 0, compare = 0, count = 0;
      this.querySelectorAll('.bundle-deals__product-js').forEach(row => {
        const box = row.querySelector('.bundle-deals__checkbox-js'); if (!box) return;
        const group = row.querySelector('.bundle-deals__variant-selects-js');
        const price = Math.max(0, Math.round(O.int(box.dataset.price) * Number(group?.dataset.percentageLeft ?? 1) - O.int(group?.dataset.fixedDiscount)));
        const comp = Math.max(price, O.int(box.dataset.comparePrice));
        if (this.dataset.updatePrices !== 'false') row.querySelectorAll('.bundle-deals__price-js').forEach(el => { el.textContent = O.money(price); }); if (this.dataset.updatePrices !== 'false') row.querySelectorAll('.bundle-deals__compare-price-js').forEach(el => { el.textContent = O.money(comp); el.hidden = comp <= price; });
        box.dataset.checked = String(box.checked); row.classList.toggle('bundle-deals__product--disabled', !box.checked);
        if (box.checked) { base += price; compare += comp; count++; }
      });
      this.totals = { price: Math.max(0, Math.round(base * Number(this.dataset.percentageLeft ?? 1) - O.int(this.dataset.fixedDiscount))), compare, quantity: count };
      if (this.dataset.updatePrices !== 'false') this.querySelectorAll('.bundle-deals__total-price-js').forEach(el => { el.textContent = O.money(this.totals.price); });
      if (this.dataset.updatePrices !== 'false') this.querySelectorAll('.bundle-deals__total-compare-price-js').forEach(el => { el.textContent = O.money(compare); el.hidden = compare <= this.totals.price; });
      let valid = true; try { this.getItems(); } catch (_) { valid = false; }
      this.querySelector('product-form')?.toggleSubmitButton?.(!valid);
    }
  }
  O.register('bundle-deals', BundleDeals);

  class ProductForm extends O.Element {
    init() {
      this.form = this.querySelector('form'); if (!this.form) return;
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.on(this.form, 'submit', event => { event.preventDefault(); this.onSubmitHandler(event); });
      this.on(this, 'change', () => this.handleErrorMessage());
    }
    buttons() {
      const set = new Set(this.form?.querySelectorAll('button[type="submit"], input[type="submit"]') || []);
      const formId = this.form?.getAttribute('id');
      if (formId) document.querySelectorAll('[form="' + CSS.escape(formId) + '"]').forEach(el => { if (el.matches('button,input[type="submit"]')) set.add(el); });
      return [...set];
    }
    toggleSubmitButton(disable = false, label = null) {
      this.unavailable = disable;
      this.buttons().forEach(button => {
        button.disabled = Boolean(disable || this.busy); button.setAttribute('aria-disabled', String(Boolean(disable || this.busy)));
        // Prefer the text leaf: the label container also owns price, icons and a second line.
        let node = button.querySelector('[data-ownstore-atc-text]') || button.querySelector('.main-atc__label__text');
        if (!node && button === this.submitButton) node = [...button.querySelectorAll('span')].find(el => !el.children.length && el.textContent.trim() && !el.closest('.main-atc-price'));
        if (node) { if (!node.dataset.originalLabel) node.dataset.originalLabel = node.dataset.ownstoreAvailableLabel || node.textContent; node.textContent = label || node.dataset.originalLabel; }
        button.querySelectorAll('.main-atc-price,.sticky-atc__button-price').forEach(price => price.classList.toggle('hidden', disable));
      });
    }
    handleErrorMessage(message = '') { visibleError(this, message); O.emit('ownstore:product-form-error', { formId: this.form?.getAttribute('id'), message }, this); }
    setBusy(busy) {
      this.busy = busy; this.setAttribute('aria-busy', String(busy));
      this.buttons().forEach(button => { button.disabled = busy || Boolean(this.unavailable); button.setAttribute('aria-disabled', String(button.disabled)); button.classList.toggle('loading', busy); button.querySelector('.loading-overlay__spinner')?.classList.toggle('hidden', !busy); });
    }
    isAuxiliary() { return Boolean(this.closest('main-product-upsell,cart-drawer-upsell,cart-drawer-gift,quantity-gifts,quantity-break-upsells') || O.bool(this.dataset.isCartUpsell)); }
    buildItems() {
      if (!this.form) throw new Error(text('product_form_not_ready', 'Product form is not ready. Please reload the page.'));
      const data = new FormData(this.form), owner = this.isAuxiliary() ? null : O.owner(this), bundle = this.closest('bundle-deals');
      let properties = {}; for (const [key, value] of data) { const m = key.match(/^properties\[(.+)\]$/); if (m && (typeof value !== 'string' || value !== '')) properties[m[1]] = value; }
      const root = owner || bundle;
      const fields = root ? [...root.querySelectorAll('custom-product-field')].filter(field => !field.closest('main-product-upsell,cart-drawer-upsell')) : [];
      const invalid = fields.filter(field => !field.validate?.(true));
      if (invalid.length) { invalid[0].querySelector('input,textarea,select')?.focus(); throw new Error(invalid[0].dataset.atcErrorMsg || text('product_fields_required', 'Please complete the required product fields.')); }
      fields.forEach(field => Object.assign(properties, field.property?.() || {}));
      let items;
      if (bundle) items = bundle.getItems();
      else if (owner?.querySelector('quantity-breaks')) items = owner.querySelector('quantity-breaks').getItems();
      else {
        const id = data.get('id'), quantity = Math.max(1, owner?.getQuantity?.() || O.int(data.get('quantity'), 1));
        const variant = owner?.getVariant?.(id);
        if (owner?.assertClassicQuantity) owner.assertClassicQuantity(variant, quantity);
        else if (variant && !variant.available) throw new Error(text('soldOut', 'Sold out'));
        if (!owner?.assertClassicQuantity && variant?.quantity_rule) {
          const rule = variant.quantity_rule, min = rule.min || 1, step = rule.increment || 1;
          if (quantity < min || quantity % step || rule.max && quantity > rule.max) throw new Error(text('quantity_not_allowed', 'Please select a quantity allowed for this product.'));
        }
        items = [{ id, quantity }];
      }
      const subscription = owner?.querySelector('shrine-subscription-widget');
      let sellingPlan = subscription?.getPlanId?.() || (!subscription ? data.get('selling_plan') : '');
      if (subscription) subscription.validateVariants(items.map(item => owner.getVariant(item.id)).filter(Boolean));
      else if (owner?.product?.requires_selling_plan && !sellingPlan) throw new Error(text('subscription_selection_required', 'Please select a subscription plan for this product.'));
      const upsell = this.closest('main-product-upsell,cart-drawer-upsell');
      if (upsell) properties._ownstore_upsell = upsell.dataset.handle;
      items = items.map(item => ({ ...item, ...(Object.keys(properties).length ? { properties: { ...properties } } : {}), ...(sellingPlan ? { selling_plan: sellingPlan } : {}) }));
      if (owner) {
        owner.querySelectorAll('main-product-upsell').forEach(el => { if (el.dataset.style !== 'add_button' && O.bool(el.dataset.selected)) items.push(...el.getItems()); });
        owner.querySelectorAll('quantity-gifts,quantity-break-upsells').forEach(el => items.push(...(el.getItems?.() || [])));
      }
      return aggregate(items);
    }
    async onSubmitHandler(event, { buyNow = false } = {}) {
      event?.preventDefault?.(); if (this.busy || this.unavailable) return;
      this.handleErrorMessage();
      let items;
      try { items = this.buildItems(); } catch (e) { this.handleErrorMessage(e.message); return; }
      this.setBusy(true);
      const drawer = document.querySelector('cart-drawer'); drawer?.setActiveElement?.(event?.submitter || document.activeElement);
      try {
        const directCheckout = buyNow || Boolean(this.form.querySelector('[data-skip-cart="true"]'));
        await O.cart.add(items, { open: !directCheckout, source: this.isAuxiliary() ? 'upsell' : 'product-form' });
        const modal = this.closest('quick-add-modal'); modal?.hide?.(true);
        if (directCheckout) location.assign(O.route('checkout'));
        else if (!document.querySelector('cart-drawer,cart-notification') && O.config.cartType !== 'drawer') location.assign(O.route('cart'));
        O.emit('ownstore:product-added', { items, form: this.form.getAttribute('id') });
      } catch (e) { this.handleErrorMessage(e.message); O.report(e, 'product-add'); }
      finally { this.setBusy(false); }
    }
    getSectionsToRender() { return O.cart.sectionRequests().map(x => ({ id: x.id, selector: x.target })); }
  }
  O.register('product-form', ProductForm);

  class CartDrawer extends O.Element {
    init() {
      this.on(this, 'click', e => { if (e.target.matches('#CartDrawer-Overlay,.cart-drawer__overlay') || e.target.closest('[data-cart-close]')) { e.preventDefault(); this.close(); } });
      this.on(this, 'keydown', e => { if (e.key === 'Escape') { e.preventDefault(); this.close(); } });
      this.on(document, 'click', e => {
        const target = e.target.closest('#cart-icon-bubble,[data-open-cart]');
        const link = target?.closest('a,button,[data-open-cart]') || target;
        if (!link || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault(); this.setActiveElement(link); this.open();
        O.cart.refresh().catch(error => { this.showError(error.message); });
      });
      this.listen(O.events.cartUpdate, ({ cart }) => { if (cart) this.classList.toggle('is-empty', !cart.item_count); });
      if (this.classList.contains('active')) this.open();
    }
    setActiveElement(el) { this.activeElement = el; }
    open(trigger) {
      if (trigger) this.activeElement = trigger;
      if (!this.classList.contains('active') && !this.activeElement) this.activeElement = document.activeElement;
      const inner = this.querySelector('.drawer__inner') || this.querySelector('[role="dialog"]') || this;
      this.classList.add('animate', 'active'); this.setAttribute('aria-hidden', 'false'); document.body.classList.add('overflow-hidden');
      if (this.trapped !== inner) { if (this.trapped) O.removeTrapFocus(); O.trapFocus(inner, inner.querySelector('button') || inner); this.trapped = inner; }
      O.emit('ownstore:cart-open');
    }
    close() {
      this.classList.remove('active'); this.setAttribute('aria-hidden', 'true');
      if (this.trapped) { O.removeTrapFocus(this.activeElement); this.trapped = null; }
      if (!document.querySelector('modal-dialog[open],quick-add-modal[open],.popup-modal--active')) document.body.classList.remove('overflow-hidden');
      this.activeElement = null;
    }
    renderContents(state) { O.cart.state = state; O.cart.render(state, state.sections || {}, true); }
    getSectionsToRender() { return O.cart.sectionRequests().map(x => ({ id: x.id, selector: x.target })); }
    getSectionInnerHTML(html, selector = '.shopify-section') { return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || ''; }
    showError(msg) { visibleError(this.querySelector('.drawer__inner') || this, msg); }
    destroy() { if (this.trapped) this.close(); }
  }
  O.register('cart-drawer', CartDrawer);

  class CartItems extends O.Element {
    init() {
      this.debouncedChange = O.debounce(event => this.onChange(event), 250);
      this.on(this, 'change', e => { if (e.target.matches('input[name="updates[]"],input[data-index][type="number"]')) this.debouncedChange(e); });
    }
    keyFor(index, input) {
      return input?.dataset.key || this.querySelector('[data-index="' + CSS.escape(String(index)) + '"][data-key]')?.dataset.key || O.cart.state?.items?.[Number(index) - 1]?.key;
    }
    async onChange(event) {
      const input = event.target; if (!input?.isConnected) return;
      let quantity = Number(input.value), minimum = Number(input.min || 0), maximum = input.max ? Number(input.max) : Infinity, step = Number(input.step || 1);
      if (!Number.isInteger(quantity) || quantity < minimum || quantity > maximum || quantity && quantity % step) { input.reportValidity(); return; }
      await this.updateQuantity(input.dataset.index, quantity, input.name, input);
    }
    async updateQuantity(index, quantity, name, input) {
      const key = this.keyFor(index, input);
      if (!key) { visibleError(this, text('cart_item_unidentified', 'The cart item could not be identified. Refresh the cart before changing it.')); return; }
      const row = input?.closest('.cart-item') || this.querySelector('[data-index="' + CSS.escape(String(index)) + '"]');
      row?.classList.add('loading'); if (input) input.disabled = true;
      try { await O.cart.change(key, quantity, { source: 'cart-items' }); }
      catch (e) { visibleError(this, e.message); if (input) { const current = O.cart.state?.items?.find(i => i.key === key); if (current) input.value = current.quantity; } }
      finally { row?.classList.remove('loading'); if (input?.isConnected) input.disabled = false; }
    }
    getSectionsToRender() { return O.cart.sectionRequests(); }
    onCartUpdate() { return O.cart.refresh(); }
    destroy() { this.debouncedChange?.cancel?.(); }
  }
  O.register('cart-items', CartItems);
  O.register('cart-drawer-items', class extends CartItems {});
  class CartRemove extends O.Element {
    init() { this.on(this, 'click', e => { e.preventDefault(); const items = this.closest('cart-items,cart-drawer-items'); items?.updateQuantity(this.dataset.index, 0, '', this); }); }
  }
  O.register('cart-remove-button', CartRemove);
  class CartNote extends O.Element {
    init() { const field = this.querySelector('textarea,input'); this.save = O.debounce(() => O.cart.note(field.value).catch(e => visibleError(this, e.message)), 500); this.on(field, 'input', this.save); }
    destroy() { this.save?.cancel?.(); }
  }
  O.register('cart-note', CartNote);

  class Upsell extends O.Element {
    init() {
      this.variants = O.variants(this); this.cartMode = this.localName === 'cart-drawer-upsell';
      this.on(this, 'change', e => { if (e.target.matches('select')) this.variantChanged(); });
      this.on(this, 'click', async e => {
        const toggle = e.target.closest('.upsell-toggle-btn'); if (!toggle || e.target.closest('select,option,a') || this.busy) return;
        e.preventDefault();
        if (!this.cartMode) { this.dataset.selected = String(!O.bool(this.dataset.selected)); this.update(); return; }
        this.busy = true;
        try {
          const state = O.cart.state || await O.cart.read();
          const existing = state.items.find(i => i.properties?._ownstore_upsell === this.dataset.handle && idEqual(i.variant_id || i.id, this.dataset.id));
          if (existing) await O.cart.change(existing.key, 0, { source: 'upsell-toggle' });
          else await O.cart.add(this.getItems(), { source: 'upsell-toggle', open: true });
        } catch (e) { visibleError(this, e.message); }
        finally { this.busy = false; }
      });
      if (this.cartMode) this.listen(O.events.cartUpdate, ({ cart }) => { this.syncCart(cart); });
      if (this.variants.length) this.variantChanged(); this.syncCart(O.cart.state); this.update();
    }
    syncCart(cart) { if (!this.cartMode || !cart) return; this.dataset.selected = String(cart.items.some(item => item.properties?._ownstore_upsell === this.dataset.handle && idEqual(item.variant_id || item.id, this.dataset.id))); this.update(); }
    variantChanged() {
      const v = O.updateOptions(this.querySelector('.upsell__variant-picker') || this, this.variants, O.bool(this.dataset.skipUnavailable));
      if (!v) return;
      this.dataset.id = v.id; this.dataset.available = String(v.available);
      this.querySelectorAll('input[name="id"]').forEach(input => { input.value = v.id; });
      this.querySelector('product-form')?.toggleSubmitButton?.(!v.available, v.available ? null : text('soldOut', 'Sold out'));
      const price = Math.max(0, Math.round(v.price * Number(this.dataset.percentageLeft ?? 1) - O.int(this.dataset.fixedDiscount)));
      if (this.dataset.updatePrices === 'true') { this.querySelectorAll('.upsell__price .regular-price').forEach(el => { el.textContent = O.money(price); }); this.querySelectorAll('.upsell__price .compare-price').forEach(el => { el.textContent = O.money(Math.max(v.price, v.compare_at_price || 0)); el.hidden = Math.max(v.price, v.compare_at_price || 0) <= price; }); }
      const image = this.querySelector('.upsell__image__img'); if (image && v.featured_image?.src) { image.src = v.featured_image.src; image.removeAttribute('srcset'); }
    }
    update() {
      this.classList.toggle('upsell--selected', O.bool(this.dataset.selected));
      this.querySelectorAll('.upsell-toggle-btn').forEach(button => { button.setAttribute('aria-pressed', this.dataset.selected || 'false'); if (button.tagName === 'BUTTON') button.type = 'button'; });
    }
    getItems() { if (this.dataset.available === 'false') throw new Error(text('addon_unavailable', 'The selected add-on is unavailable.')); return [{ id: this.dataset.id, quantity: 1, properties: { _ownstore_upsell: this.dataset.handle } }]; }
  }
  O.register('main-product-upsell', Upsell);
  O.register('cart-drawer-upsell', class extends Upsell {});

  class CartGift extends O.Element {
    init() { this.variants = O.variants(this); this.on(this, 'change', e => { if (e.target.matches('select')) { const v = O.updateOptions(this.querySelector('.upsell__variant-picker') || this, this.variants, true); if (v) this.dataset.id = v.id; } }); }
    getRule() { return { parent: this.dataset.mainProduct || '', min: O.int(this.dataset.requirement, 1), unlockBy: this.dataset.type === 'cart_subtotal' ? 'cart_subtotal' : 'quantity' }; }
  }
  O.register('cart-drawer-gift', CartGift);

  const appliedCodes = state => [...new Set([
    ...(state?.discount_codes || []).filter(c => c.applicable !== false).map(c => c.code),
    ...(state?.cart_level_discount_applications || []).filter(c => c.type === 'discount_code').map(c => c.title),
    ...(state?.items || []).flatMap(i => (i.line_level_discount_allocations || []).filter(d => d.discount_application?.type === 'discount_code').map(d => d.discount_application.title))
  ].filter(Boolean))];
  const codeApplied = (state, code) => appliedCodes(state).some(x => x.toLowerCase() === code.toLowerCase());
  O.appliedDiscountCodes = appliedCodes;
  class CartDiscount extends O.Element {
    init() { this.on(this.querySelector('form'), 'submit', e => { e.preventDefault(); this.apply(); }); this.reset(); }
    reset() { this.querySelectorAll('.cart-discount-form__error,.cart-discount-form__success').forEach(el => { el.hidden = true; el.style.display = 'none'; }); }
    message(type) { this.reset(); const el = this.querySelector(type === 'success' ? '.cart-discount-form__success' : '.cart-discount-form__error--' + type); if (el) { el.hidden = false; el.style.display = 'block'; } }
    async apply() {
      if (this.busy) return;
      const field = this.querySelector('input'), code = field?.value.trim(); this.reset();
      if (!code) { this.message('empty'); field?.focus(); return; }
      const current = O.cart.state, codes = current ? appliedCodes(current) : (this.dataset.appliedCodes || '').split(',').filter(Boolean);
      if (codes.some(c => c.toLowerCase() === code.toLowerCase())) { this.message('duplicate'); return; }
      this.busy = true; const button = this.querySelector('button'); if (button) { button.disabled = true; button.classList.add('loading'); } this.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');
      const fieldId = field.id;
      try {
        const state = await O.cart.discount([...codes, code], { source: 'cart-discount' });
        const active = domId(fieldId)?.closest('cart-discount-block') || this;
        if (codeApplied(state, code)) { active.message('success'); if (domId(fieldId)) domId(fieldId).value = ''; const timeout = setTimeout(() => active.reset(), Math.max(1, Number(active.dataset.successDuration) || 5) * 1000); active.cleanups?.push(() => clearTimeout(timeout)); }
        else active.message('invalid');
      } catch (e) { const active = domId(fieldId)?.closest('cart-discount-block') || this; active.message(e.status ? 'invalid' : 'network'); }
      finally { this.busy = false; if (button?.isConnected) { button.disabled = false; button.classList.remove('loading'); this.querySelector('.loading-overlay__spinner')?.classList.add('hidden'); } }
    }
  }
  O.register('cart-discount-block', CartDiscount);
  class ClickableDiscount extends O.Element {
    init() { this.on(this.querySelector('button'), 'click', e => { e.preventDefault(); this.apply(); }); }
    async apply() {
      if (this.dataset.loading === 'true') return;
      this.dataset.loading = 'true'; this.dataset.error = 'false';
      try {
        const state = O.cart.state || await O.cart.read();
        const result = await O.cart.discount([...appliedCodes(state), this.dataset.code], { source: 'clickable-discount' });
        const success = codeApplied(result, this.dataset.code); this.dataset.applied = String(success); this.dataset.error = String(!success); this.querySelector('button')?.setAttribute('aria-pressed', String(success));
      } catch (e) { this.dataset.error = 'true'; O.report(e, 'clickable-discount'); }
      finally { this.dataset.loading = 'false'; }
    }
  }
  O.register('clickable-discount', ClickableDiscount);

  // Keep accelerated single-item payment out of a multi-item package request.
  document.addEventListener('ownstore:quantity-change', e => {
    const owner = e.target.closest?.('product-info'); if (!owner) return;
    const complex = (owner.querySelector('quantity-breaks')?.getQuantity?.() || 1) > 1 || Boolean(owner.querySelector('quantity-gifts,main-product-upsell[data-selected="true"]')); 
    owner.querySelectorAll('.shopify-payment-button').forEach(el => { el.hidden = complex; });
  });
  document.addEventListener('click', e => {
    const button = e.target.closest('[id^="SectionAtcBtn-"],[data-ownstore-add-to-cart]');
    if (!button || button.disabled || e.defaultPrevented) return;
    const sticky = button.closest('sticky-atc');
    const section = button.dataset.section || sticky?.dataset.section || button.id.replace(/^SectionAtcBtn-/, '');
    const form = button.form?.closest('product-form') || O.owner(button)?.querySelector('product-form[data-main="true"],product-form.main-product-form') || domId('ProductForm-' + section) || domId('product-form-' + section)?.closest('product-form') || (!sticky ? document.querySelector('product-form[data-main="true"]') : null);
    if (!form?.onSubmitHandler) return;
    e.preventDefault(); form.onSubmitHandler(e, { buyNow: O.bool(button.dataset.skipCartButton) });
  });
  document.addEventListener('ownstore:cart-pending', e => {
    document.querySelectorAll('button[name="checkout"]').forEach(button => {
      if (e.detail.pending > 0) { if (!button.hasAttribute('data-ownstore-was-disabled')) button.dataset.ownstoreWasDisabled = String(button.disabled); button.disabled = true; }
      else if (button.hasAttribute('data-ownstore-was-disabled')) { button.disabled = button.dataset.ownstoreWasDisabled === 'true'; delete button.dataset.ownstoreWasDisabled; }
    });
  });
})();
